/**
 * Deterministic per-article presentation variation.
 *
 * Every article on LifeWestWay shares the same underlying blocks (key findings,
 * FAQ, editorial disclaimer, author box). Rendering all of them in an identical
 * order with identical headings makes the library read as machine-templated.
 * These helpers derive a *stable* variant from the article slug, so a given URL
 * always renders the same way (no hydration mismatch, no layout churn between
 * builds) while the library as a whole looks hand-assembled.
 *
 * Only presentation varies here — no factual content is added, removed or
 * reworded by this module.
 */

/** Stable 32-bit FNV-1a hash so variants never change between builds. */
export function slugHash(slug: string, salt = ""): number {
  let h = 0x811c9dc5;
  const input = `${salt}:${slug}`;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export function pickVariant<T>(slug: string, salt: string, options: readonly T[]): T {
  return options[slugHash(slug, salt) % options.length];
}

const FAQ_HEADINGS = [
  "Frequently Asked Questions",
  "Common Questions, Answered",
  "Questions Readers Ask Most",
  "Quick Answers",
  "Still Wondering?",
] as const;

const KEY_FINDINGS_HEADINGS = [
  "Key Findings",
  "The Short Version",
  "What You Need to Know",
  "Key Points at a Glance",
  "Before You Read On",
] as const;

const AUTHOR_LABELS = [
  "Written & fact-checked by",
  "Researched and verified by",
  "Reported by",
  "Checked against primary sources by",
] as const;

/** Where the summary block sits relative to the intro paragraph. */
export type SummaryPlacement = "beforeIntro" | "afterIntro";

/** Order of the closing blocks under the article body. */
export type EndMatterOrder = "faq-disclaimer-bio" | "bio-faq-disclaimer" | "faq-bio-disclaimer";

/** Where the editor's note sits. */
export type NotePlacement = "afterSummary" | "beforeFaq";

export const faqHeading = (slug: string) => pickVariant(slug, "faq", FAQ_HEADINGS);
export const keyFindingsHeading = (slug: string) =>
  pickVariant(slug, "summary", KEY_FINDINGS_HEADINGS);
export const authorLabel = (slug: string) => pickVariant(slug, "author", AUTHOR_LABELS);

export const summaryPlacement = (slug: string): SummaryPlacement =>
  pickVariant(slug, "summary-pos", ["afterIntro", "afterIntro", "beforeIntro"] as const);

export const endMatterOrder = (slug: string): EndMatterOrder =>
  pickVariant(slug, "endmatter", [
    "faq-disclaimer-bio",
    "bio-faq-disclaimer",
    "faq-bio-disclaimer",
  ] as const);

export const notePlacement = (slug: string): NotePlacement =>
  pickVariant(slug, "note-pos", ["afterSummary", "beforeFaq"] as const);
